import React, { useMemo } from "react";
import {
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, isSameDay } from "date-fns";
import type { LamShiftRollsRow } from "../../../api/lamLine";

/**
 * Which shift the timeline is showing, and how to get to another one.
 *
 * Two ways in, because there are two ways people look for a shift. The arrows
 * step to the one before or after — how you follow a problem backwards, or
 * compare a crew with the one that handed over to it. The date and shift
 * controls jump straight to a known one, which is how you answer "what
 * happened on the 3rd".
 *
 * The line does not run every day, so the calendar disables days with nothing
 * on them rather than letting someone land on an empty timeline and read it as
 * a bad shift. Today is the exception — see ShiftSlot.
 */

/**
 * One selectable shift.
 *
 * `row` is absent for a shift that is scheduled but has not run: today's night
 * shift exists as a slot from midnight, so it can be selected before there is
 * anything in it. That one is deliberately blank rather than hidden — a shift
 * you cannot see coming is harder to reason about than an empty one.
 */
export type ShiftSlot = {
  /** Slot start, Nashville wall-clock as a local Date. Sorts the browse list. */
  start: Date;
  /** Button label — "Day", "Night". */
  label: string;
  row?: LamShiftRollsRow;
};

/**
 * "Day Shift" as a button reads as a sentence; "Day" reads as a choice. Names
 * come from settings_shifts, so a shift named something else keeps its own
 * name, and one that could not be named at all keeps its start time.
 */
export const shiftLabel = (name: string) =>
  name.replace(/\s*shifts?\s*$/i, "").trim() || name;

const ShiftBrowser: React.FC<{
  /** Every selectable shift, oldest-first. */
  slots: ShiftSlot[];
  /** Index into `slots`. -1 when there are none. */
  index: number;
  onIndexChange: (index: number) => void;
}> = ({ slots, index, onIndexChange }) => {
  const current = slots[index];

  // Days that hold at least one slot, so the calendar can grey out the rest.
  const days = useMemo(() => slots.map((s) => s.start), [slots]);

  // The slots on the day currently in view — what the shift buttons offer.
  const sameDay = useMemo(() => {
    if (!current) return [];
    return slots
      .map((slot, i) => ({ slot, i }))
      .filter(({ slot }) => isSameDay(slot.start, current.start));
  }, [slots, current]);

  const pickDate = (date: Date | null) => {
    if (!date || Number.isNaN(date.getTime())) return;
    // Land on the day's first shift; the buttons move within the day.
    const hit = slots.findIndex((s) => isSameDay(s.start, date));
    if (hit >= 0) onIndexChange(hit);
  };

  const atOldest = index <= 0;
  const atNewest = index < 0 || index >= slots.length - 1;

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
        aria-hidden
      >
        Shift:
      </Typography>

      {/* A disabled IconButton drops its tooltip with it, so the span keeps the
          hover target alive to explain why the arrow is dead. */}
      <Tooltip title={atOldest ? "No earlier shift recorded" : "Previous shift"}>
        <span>
          <IconButton
            size="small"
            aria-label="Previous shift"
            disabled={atOldest}
            onClick={() => onIndexChange(index - 1)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Date"
          value={current ? current.start : null}
          onChange={pickDate}
          minDate={days[0]}
          maxDate={days[days.length - 1]}
          shouldDisableDate={(day: Date) => !days.some((d) => isSameDay(d, day))}
          slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
        />
      </LocalizationProvider>

      {/* Buttons rather than a dropdown: a day holds two or three shifts, and
          all of them fit on screen — so which ones exist, and which one you
          are on, are visible without opening anything. */}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={current ? String(index) : null}
        onChange={(_, v: string | null) => {
          if (v !== null) onIndexChange(Number(v));
        }}
        aria-label="Shift"
      >
        {sameDay.map(({ slot, i }) => (
          <ToggleButton
            key={slot.start.getTime()}
            value={String(i)}
            title={
              slot.row
                ? `Starts ${format(slot.start, "HH:mm")}`
                : `Starts ${format(slot.start, "HH:mm")} — no rolls recorded`
            }
            // Nothing in it yet, so it reads as available rather than current.
            sx={slot.row ? undefined : { fontStyle: "italic" }}
          >
            {slot.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Tooltip title={atNewest ? "This is the latest shift" : "Next shift"}>
        <span>
          <IconButton
            size="small"
            aria-label="Next shift"
            disabled={atNewest}
            onClick={() => onIndexChange(index + 1)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};

export default ShiftBrowser;
